---
title: "Posting jobs to free job boards"
description: "Post a Chosen job to Google for Jobs and free job sites with one switch, see when each board picks it up, and which board every applicant came from."
category: "careers"
keywords:
  - "post job to job boards"
  - "google for jobs"
  - "free job posting sites"
  - "job board distribution"
  - "applicant source tracking"
schema: "HowTo"
nav:
  section: "guides"
  order: 15
  parent: "/guides/careers"
---

One switch per job sends it to Google for Jobs and to the free job sites that read Chosen's job feed. Applicants still apply on your careers page, land in the job's pipeline, and carry the name of the board that sent them.

Nothing is posted until you turn it on. Every job starts with the switch off, including the ones you already have.

## Turn it on for a job

<Steps>
  <li>Open the job and go to the **Job Boards** tab.</li>
  <li>Switch on **Post to free job boards**.</li>
  <li>Click **Update Position**. New jobs work the same way: flip the switch before **Create Position**.</li>
</Steps>

A small megaphone next to the job in the sidebar means it's posted.

## What boards need first

The tab's **How boards will read it** section checks the job as you type. Boards turn away jobs that miss any of these, so Chosen doesn't send them:

- **A published careers page and an Open job.** Boards link to your careers page, so it has to be public. Internal and closed jobs never go out.
- **A real description.** At least 200 characters, and not the starter text a new job comes with.
- **A location boards can read.** `Austin, TX`, `London, UK` or `Toronto, ON, Canada` all work. For remote jobs set **Work Location** to Remote and name the country, like `Remote (US)`. "Remote" with no country is read as the United States.

A pay range is optional, but boards rank jobs that show one higher, and California, Colorado, New York, Washington and other states require it. Write it the way you would in a posting: `$120,000 - $150,000`, `$45 - $60/hr`.

Boards take one location per job. If the location lists several ("SF or NYC"), only the first is sent.

## Where your job goes

**Google for Jobs** reads the job straight from its page on your careers site. The rest of the boards in the tab pull Chosen's job feed on their own schedule. The list grows as more boards connect, and the tab always shows the current one.

## What the statuses mean

| Status | What it means |
| --- | --- |
| On the job page | Google can read the job. New jobs usually show up in Google within a few days. |
| Waiting for next visit | The job is in the feed. The board hasn't fetched it since you turned it on or last edited it. |
| Picked up | The board fetched the latest version of the job, at the time shown. |
| Paused | Your careers page is hidden or the job isn't Open. Fix that and it goes back out. |
| Not listed | Something in the check above needs fixing. |
| Off | The switch is off. |

Boards fetch new jobs every few hours, give or take. Edits and removals reach them the same way, on their next visit. They run on their own clocks, not yours.

## See which board sent each applicant

Applicants from a board get that board's name as their **Source**, so "Indeed" or "Google for Jobs" shows up in the candidates table and in source reporting. The tab also counts the applicants each board sent.

The same works for links you share yourself. Add `?utm_source=` to a careers link (for example `?utm_source=newsletter`) and those applicants get the Source "Career page · newsletter".

## Take a job off the boards

Switch it off and save. Closing the job, setting it to Internal or hiding your careers page does the same. Boards drop the job on their next visit, usually within a day. Applicants who already applied stay in the job.

## Limits

- **Free boards only, for now.** Chosen doesn't buy sponsored or premium placements on any board.
- **Boards decide what they show.** Each board runs its own quality checks and can skip a job that passes Chosen's.
- **One location per job.**
